import { primitives } from './primitives.ts';

const { color, space, fontSize, fontWeight, lineHeight, radius, shadow, motion } = primitives;

const LATIN_STACK = "'Inter', system-ui, sans-serif";
const ARABIC_STACK = "'Cairo', 'Noto Sans Arabic', system-ui, sans-serif";

const shared = {
  font: {
    heading: LATIN_STACK,
    body: LATIN_STACK,
    arabic: ARABIC_STACK,
    size: fontSize,
    weight: fontWeight,
    lineHeight,
  },
  space,
  radius,
  motion,
} as const;

export const lightTokens = {
  ...shared,
  color: {
    brand: color.blue[600],
    brandHover: color.blue[700],
    brandSubtle: color.blue[50],
    brandContrast: color.neutral[0],
    accent: color.slate[500],
    accentHover: color.slate[600],
    accentContrast: color.neutral[0],
    bg: color.neutral[50],
    surface: color.neutral[0],
    surfaceRaised: color.neutral[0],
    surfaceSunken: color.neutral[100],
    hover: color.neutral[75],
    inverse: color.slate[500],
    inverseContrast: color.neutral[0],
    text: color.neutral[1000],
    muted: color.neutral[500],
    border: color.neutral[200],
    field: color.neutral[25],
    switchThumb: color.neutral[0],
    fieldBorder: color.neutral[300],
    focusHalo: 'rgba(26, 115, 232, 0.22)',
    dangerHalo: 'rgba(211, 47, 47, 0.18)',
    success: color.green[500],
    successSubtle: color.green[100],
    successText: color.green[700],
    warning: color.orange[500],
    warningSubtle: color.orange[100],
    warningText: color.orange[700],
    danger: color.red[500],
    dangerSubtle: color.red[100],
    dangerText: color.red[700],
    info: color.blue[500],
    infoSubtle: color.blue[50],
    infoText: color.blue[700],
    focusRing: color.blue[500],
    overlay: 'rgba(25, 25, 25, 0.45)',
    brandSurfaceFrom: color.charcoal[500],
    brandSurfaceTo: color.charcoal[900],
    onBrandSurface: color.neutral[0],
    onBrandSurfaceMuted: color.neutral[350],
    brandSurfaceHover: 'rgba(255, 255, 255, 0.07)',
    brandSurfaceActive: 'rgba(255, 255, 255, 0.13)',
    brandSurfaceHighlight: color.blue[400],
    chart1: color.neutral[500],
    chart2: color.blue[500],
    chart3: color.orange[500],
    chart4: color.slate[500],
    chart5: color.red[500],
    chart6: color.green[500],
    series1: '#2A78D6',
    series2: '#EB6834',
    series3: '#1BAF7A',
    series4: '#EDA100',
    series5: '#E87BA4',
    series6: '#6250D6',
    seriesOther: color.neutral[400],
    heat1: color.blue[50],
    heat2: color.blue[100],
    heat3: color.blue[300],
    heat4: color.blue[500],
    heat5: color.blue[700],
  },
  shadow,
} as const;

export const darkTokens = {
  ...shared,
  color: {
    brand: color.blue[400],
    brandHover: color.blue[300],
    brandSubtle: color.blue[900],
    brandContrast: color.neutral[900],
    accent: color.slate[300],
    accentHover: color.slate[200],
    accentContrast: color.neutral[900],
    bg: color.neutral[900],
    surface: color.neutral[800],
    surfaceRaised: color.neutral[750],
    surfaceSunken: color.neutral[750],
    hover: color.neutral[750],
    inverse: color.neutral[950],
    inverseContrast: color.neutral[900],
    text: color.neutral[950],
    muted: color.neutral[400],
    border: color.neutral[700],
    field: color.neutral[750],
    switchThumb: color.neutral[950],
    fieldBorder: color.neutral[650],
    focusHalo: 'rgba(73, 163, 241, 0.32)',
    dangerHalo: 'rgba(240, 122, 136, 0.24)',
    success: '#5CC98A',
    successSubtle: '#16302A',
    successText: '#8FDDB0',
    warning: '#E6B05C',
    warningSubtle: '#33291A',
    warningText: '#F0C98A',
    danger: '#F07A88',
    dangerSubtle: '#3A1C24',
    dangerText: '#F7A9B3',
    info: color.blue[300],
    infoSubtle: color.blue[900],
    infoText: color.blue[100],
    focusRing: color.blue[300],
    overlay: 'rgba(0, 0, 0, 0.6)',
    brandSurfaceFrom: color.charcoal[700],
    brandSurfaceTo: color.charcoal[950],
    onBrandSurface: color.neutral[0],
    onBrandSurfaceMuted: color.neutral[350],
    brandSurfaceHover: 'rgba(255, 255, 255, 0.06)',
    brandSurfaceActive: 'rgba(255, 255, 255, 0.11)',
    brandSurfaceHighlight: color.blue[400],
    chart1: color.neutral[400],
    chart2: color.blue[300],
    chart3: '#E6B05C',
    chart4: color.slate[300],
    chart5: '#F07A88',
    chart6: '#5CC98A',
    series1: '#3987E5',
    series2: '#D95926',
    series3: '#199E70',
    series4: '#C98500',
    series5: '#D55181',
    series6: '#9085E9',
    seriesOther: color.neutral[400],
    heat1: color.neutral[750],
    heat2: color.blue[800],
    heat3: color.blue[600],
    heat4: color.blue[500],
    heat5: color.blue[400],
  },
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    md: '0 1px 2px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25)',
    lg: '0 16px 40px rgba(0, 0, 0, 0.45)',
    field: 'inset 0 1px 2px rgba(0, 0, 0, 0.25)',
  },
} as const;

export type SemanticTokens = typeof lightTokens;
